import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Handshake, 
  TrendUp, 
  CheckCircle,
  ArrowRight
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const PartnerProcess = () => {
  const steps = [
    {
      number: "01",
      icon: <Users className="w-8 h-8" weight="thin" />,
      title: "Klant Aanbrengen",
      description: "Breng een klant aan die interesse heeft in onze diensten. Dit kan via je netwerk, social media, of andere kanalen.",
      details: [
        "Geen aanmelding nodig",
        "Gebruik je eigen netwerk",
        "Flexibele aanpak"
      ],
      color: "from-blue-600 to-indigo-700",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      number: "02",
      icon: <Handshake className="w-8 h-8" weight="thin" />,
      title: "Klant Converteert",
      description: "De klant neemt contact op met ons en wordt uiteindelijk een klant van Juliux.",
      details: [
        "Wij doen de rest",
        "Geen verkoop druk",
        "Natuurlijk proces"
      ],
      color: "from-blue-700 to-indigo-800",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      number: "03",
      icon: <CheckCircle className="w-8 h-8" weight="thin" />,
      title: "Project Opgeleverd",
      description: "Het project wordt succesvol opgeleverd en de klant is tevreden.",
      details: [
        "Wij zorgen voor kwaliteit",
        "Klanttevredenheid voorop",
        "Succesvolle oplevering"
      ],
      color: "from-indigo-700 to-blue-800",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-300"
    },
    {
      number: "04",
      icon: <TrendUp className="w-8 h-8" weight="thin" />,
      title: "Direct Uitbetaald",
      description: "Je krijgt je commissie direct uitbetaald na oplevering van het project.",
      details: [
        "Geen wachttijd",
        "Direct op je rekening",
        "Geen gedoe"
      ],
      color: "from-blue-800 to-indigo-900",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-300"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="how-it-works" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-full px-4 py-2 mb-6">
            <CheckCircle className="w-5 h-5" weight="thin" />
            <span className="text-sm font-medium">Hoe het Werkt</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Van{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">
              klant aanbrengen tot uitbetaling
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Het systeem is super eenvoudig. Breng gewoon klanten aan en verdien direct na oplevering. 
            Geen gedoe, geen verplichtingen.
          </p>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-4 gap-8 mb-16"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative"
            >
              {/* Step Number */}
              <div className={`absolute -top-4 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-r ${step.color} text-white rounded-full flex items-center justify-center font-bold text-lg z-10`}>
                {step.number}
              </div>

              {/* Step Card */}
              <div className={`relative p-8 rounded-2xl ${step.bgColor} border ${step.borderColor} h-full`}>
                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-r ${step.color} rounded-2xl flex items-center justify-center text-white mb-6 mx-auto`}>
                  {step.icon}
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  {step.title}
                </h3>
                
                <p className="text-gray-600 mb-6 text-center">
                  {step.description}
                </p>

                {/* Details */}
                <ul className="space-y-3">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" weight="thin" />
                      <span className="text-sm text-gray-600">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-gray-300 to-gray-400 transform -translate-y-1/2 z-0" />
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Timeline for Mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="lg:hidden"
        >
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-indigo-500 to-purple-500"></div>
            
            {steps.map((step, index) => (
              <div key={index} className="relative flex items-start mb-8 last:mb-0">
                <div className={`w-16 h-16 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center text-white font-bold text-lg z-10 flex-shrink-0`}>
                  {step.number}
                </div>
                
                <div className="ml-6 flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {step.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" weight="thin" />
                        <span className="text-sm text-gray-600">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Klaar om partner te worden?
            </h3>
            <p className="text-gray-600 mb-6">
              Geen aanmelding nodig. Breng gewoon je eerste klant aan en begin met verdienen!
            </p>
            <Link
              to="/start-your-project"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all duration-300 transform hover:scale-105"
            >
              <span>Start met Verdienen</span>
              <ArrowRight className="w-5 h-5" weight="thin" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnerProcess;
